import { addDays, dateOf, instantAt } from '@/lib/time'
import type { BookingsSettings } from '@/features/settings/queries'

import type { BookingKind } from './queries'

/**
 * The six tiles the drive flow opens on, and the arithmetic behind each.
 *
 * The client's own measurement is that "around 90–95% of our test drives happen immediately", so
 * the first screen answers `when` before it is asked: **Now** is selected on arrival and the five
 * exceptions sit beside it. Only `later` asks for a date and a time; every other tile starts at
 * the signature and works its own return time out from the dealership's settings.
 *
 * **No new enum member.** `BookingKind` stays `test_drive | loan` — "extended", "overnight" and
 * "two or three days" are durations, and a duration is `expected_return_at`. A later change to
 * one is an `activity_extensions` row, not a different kind of drive.
 *
 * **Only `later` writes a `bookings` row.** The rest create an `activities` row with a NULL
 * `booking_id`, which is what the partial indexes were built for (ADR 0005: what holds a vehicle
 * is a booking *or* a drive).
 *
 * Pure, and nothing here reads the browser's clock or its zone: the departure arrives as an
 * instant and the dealership's zone as a parameter, exactly as `lib/time` requires.
 */

/** The tiles, in the order the first screen draws them. `now` is first because it is the default. */
export const DRIVE_PRESETS = ['now', 'later', 'extended', 'overnight', 'multi_day', 'loan'] as const

export type DrivePreset = (typeof DRIVE_PRESETS)[number]

/** Selected on arrival, so a standard walk-in reaches the driver question with no tap here. */
export const DEFAULT_PRESET: DrivePreset = 'now'

/** The word on the tile. Short enough that six of them do not wrap at 390px. */
export const PRESET_LABELS: Record<DrivePreset, string> = {
  now: 'Now',
  later: 'Book for later',
  extended: 'Extended',
  overnight: 'Overnight',
  multi_day: 'Two or three days',
  loan: 'Loan vehicle',
}

/**
 * Which kind each tile books. Five test drives and one loan: the tiles are durations, not kinds,
 * and `Loan vehicle` is the only one that is a different kind of drive.
 */
export const PRESET_KINDS: Record<DrivePreset, BookingKind> = {
  now: 'test_drive',
  later: 'test_drive',
  extended: 'test_drive',
  overnight: 'test_drive',
  multi_day: 'test_drive',
  loan: 'loan',
}

/** The one tile that plans instead of starting: the free-window picker and a `bookings` row. */
export function writesBooking(preset: DrivePreset): boolean {
  return preset === 'later'
}

/**
 * Whether the flow still has to ask when.
 *
 * The same question as `writesBooking` today and not the same question: this one is about the
 * screen (does step 2 appear at all), and that one is about the write. Keeping them apart is what
 * stops a later tile that starts now but asks for a day turning into a booking by accident.
 */
export function asksWhen(preset: DrivePreset): boolean {
  return preset === 'later'
}

/** Hours a staff member can choose for an extended drive. The middle one starts chosen. */
export const EXTENDED_HOURS = [2, 3, 4] as const
/** "Two or three days" is exactly that: the spec's own words, and the spec's own two chips. */
export const MULTI_DAY_DAYS = [2, 3] as const

/**
 * The lengths a tile offers, or `[]` when it has nothing to ask.
 *
 * `now`, `overnight` and `loan` each have exactly one answer already — the dealership's default
 * duration, its overnight return time, its default loan length — so there is nothing to choose
 * and no chip row under them.
 */
export function presetChoices(preset: DrivePreset): readonly number[] {
  if (preset === 'extended') return EXTENDED_HOURS
  if (preset === 'multi_day') return MULTI_DAY_DAYS
  return []
}

/** The chip that starts chosen on a tile that offers any. Zero when it offers none. */
export function defaultLength(preset: DrivePreset): number {
  const choices = presetChoices(preset)
  return choices[Math.floor(choices.length / 2)] ?? 0
}

/** `3` -> `3 hours` on Extended, `3 days` on two-or-three. The unit follows the tile. */
export function lengthLabel(preset: DrivePreset, length: number): string {
  const unit = preset === 'extended' ? 'hour' : 'day'
  return `${length} ${unit}${length === 1 ? '' : 's'}`
}

/** A whole day, in minutes: what the day tiles count in. */
const MINUTES_IN_A_DAY = 24 * 60

export type PresetReturnInput = {
  preset: DrivePreset
  /** When the keys change hands, as an absolute instant. The signature, in the ordinary case. */
  startsAt: string
  /** `test_drive_default_minutes` and `loan_default_days`: the dealership's own two defaults. */
  defaults: BookingsSettings
  /** `drives.overnight_return_time`: `HH:MM` or `HH:MM:SS` on the dealership's clock. */
  overnightReturn: string
  /** The dealership's IANA zone. Never the browser's. */
  timeZone: string
  /** The chip the staff member chose, where the tile offers any. Ignored where it does not. */
  length: number
}

/**
 * When a preset drive is due back, as an absolute instant.
 *
 * `later` has no answer here and returns `null`: its window comes from the free-window picker,
 * which asks the API where a length fits and answers with two instants, so that a loan across a
 * daylight-saving boundary keeps the window it will actually be stored with.
 *
 * `overnight` is the one tile whose return is a wall time rather than a length: the next day at
 * the dealership's own `overnight_return_time`, resolved through `instantAt` so the change of
 * offset a spring-forward brings is applied once rather than carried as an error.
 */
export function presetReturn({
  preset,
  startsAt,
  defaults,
  overnightReturn,
  timeZone,
  length,
}: PresetReturnInput): string | null {
  if (preset === 'later') return null
  if (preset === 'overnight') {
    return instantAt(addDays(dateOf(startsAt, timeZone), 1), overnightReturn, timeZone)
  }
  return plusMinutes(startsAt, presetMinutes({ preset, defaults, length }))
}

/**
 * How long a preset runs for, in minutes. `overnight` is not here: its return is a wall time on
 * the following day, which is a different question from a length (see `presetReturn`).
 */
export function presetMinutes({
  preset,
  defaults,
  length,
}: Pick<PresetReturnInput, 'preset' | 'defaults' | 'length'>): number {
  if (preset === 'extended') return length * 60
  if (preset === 'multi_day') return length * MINUTES_IN_A_DAY
  if (preset === 'loan') return defaults.loan_default_days * MINUTES_IN_A_DAY
  return defaults.test_drive_default_minutes
}

function plusMinutes(instant: string, minutes: number): string {
  return new Date(Date.parse(instant) + minutes * 60_000).toISOString()
}
